// ─── FlooringForm.js ──────────────────────────────────────────────────────────
// Project-level Internal Flooring register (§4) — floor coverings graded for
// replacement (vinyl, laminate, tiles, carpet). NOT the 'Floors' painting
// substrate. Site-wide, recorded once per project, same screen conventions as
// SiteItemsForm. Carries an explicit safety flag — the source condition reports
// call out trip hazards here, and that must never live only in free text.

import React, { useState, useRef, useEffect } from 'react';
import { FLOOR_PRODUCTS, FLOOR_RECOMMENDATIONS, CONDITION_LEVELS } from './areaConstants';
import { uploadPhoto } from './areaFormUtils';
import ConfirmModal from './ConfirmModal';
import { showToast } from './Toast';

function newId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function emptyEntry() {
  return {
    id: newId(), product: '', location: '', condition: null, recommendation: '',
    note: '', safetyRisk: false, safetyNote: '', photos: [],
  };
}

const CONDITION_LABEL = Object.fromEntries(CONDITION_LEVELS.map(c => [c.key, c.label]));
const RECOMMENDATION_LABEL = Object.fromEntries(FLOOR_RECOMMENDATIONS.map(r => [r.key, r.label]));

// ── Photo upload — mirrors the PM photo flow in AssessForm / SiteItemsForm ──
function PhotoUpload({ photos, projectId, onAdd, onUpdate, onRemove }) {
  const cameraRef = useRef(null);
  const libRef = useRef(null);
  const safePhotos = Array.isArray(photos) ? photos : [];

  const handleFile = (file) => {
    if (!file || !projectId) return;
    uploadPhoto(
      file, projectId,
      (tempPhoto) => onAdd({ ...tempPhoto, addedBy: 'pm' }),
      (tempId, { url, thumbnailUrl }) => onUpdate(tempId, { url, thumbnailUrl, pending: false }),
      (tempId) => { showToast('Photo upload failed — please try again', 'error'); onRemove(tempId); }
    );
  };

  return (
    <div className="si-photos">
      {safePhotos.length > 0 && (
        <div className="si-photo-thumbs">
          {safePhotos.map(p => (
            <div key={p.id} className="si-photo-thumb">
              <img src={p.thumbnailUrl || p.url} alt="" />
              {p.pending && <span className="si-photo-pending">Uploading…</span>}
              <button type="button" className="si-photo-remove" onClick={() => onRemove(p.id)} aria-label="Remove photo">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="si-photo-actions">
        <button type="button" className="si-photo-btn" onClick={() => cameraRef.current?.click()}>📷 Add Photo</button>
        <button type="button" className="si-photo-btn si-photo-lib" onClick={() => libRef.current?.click()}>📁 Library</button>
        <input type="file" accept="image/*" capture="environment" ref={cameraRef} style={{ display: 'none' }}
          onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
        <input type="file" accept="image/*" ref={libRef} style={{ display: 'none' }}
          onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
      </div>
    </div>
  );
}

function FlooringEntryCard({ entry, expanded, invalid, onToggle, onChange, onRemove, projectId }) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [otherMode, setOtherMode] = useState(!!entry.product && !FLOOR_PRODUCTS.includes(entry.product));

  if (!expanded) {
    const summary = [entry.product || 'Untitled', entry.location, CONDITION_LABEL[entry.condition], RECOMMENDATION_LABEL[entry.recommendation]]
      .filter(Boolean).join(' · ');
    return (
      <div className={`fl-card fl-card-collapsed ${invalid ? 'fl-card-invalid' : ''}`} onClick={onToggle}>
        <div className="fl-summary">{summary}</div>
        {entry.safetyRisk && <span className="fl-safety-badge">⚠ Safety</span>}
      </div>
    );
  }

  return (
    <div className={`fl-card ${invalid ? 'fl-card-invalid' : ''}`}>
      <div className="fl-card-header" onClick={onToggle}>
        <div className="fl-card-title">{entry.product || 'New floor covering'}</div>
        <span className="fl-collapse-btn">▲</span>
      </div>

      <div className="fl-field">
        <label>Product</label>
        <select className="fl-select" value={otherMode ? '__other__' : (entry.product || '')}
          onChange={e => {
            if (e.target.value === '__other__') { setOtherMode(true); onChange({ product: '' }); }
            else { setOtherMode(false); onChange({ product: e.target.value }); }
          }}>
          <option value="">Select a product…</option>
          {FLOOR_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
          <option value="__other__">Other…</option>
        </select>
        {otherMode && (
          <input className="fl-text-input" type="text" placeholder="Describe the product" style={{ marginTop: 6 }}
            value={entry.product || ''} onChange={e => onChange({ product: e.target.value })} />
        )}
      </div>

      <div className="fl-field">
        <label>Location</label>
        <input className="fl-text-input" type="text" placeholder="e.g. Cot room, hallway, wet areas"
          value={entry.location || ''} onChange={e => onChange({ location: e.target.value })} />
      </div>

      <div className="as-condition-row">
        <span className="as-condition-label">Condition</span>
        <div className="as-condition-pills">
          {CONDITION_LEVELS.map(c => (
            <button key={c.key} type="button"
              className={`as-cond-pill as-cond-${c.key} ${entry.condition === c.key ? 'active' : ''}`}
              onClick={() => onChange({ condition: entry.condition === c.key ? null : c.key })}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="fl-field">
        <label>Recommendation</label>
        <select className="fl-select" value={entry.recommendation || ''}
          onChange={e => onChange({ recommendation: e.target.value })}>
          <option value="">Select…</option>
          {FLOOR_RECOMMENDATIONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </div>

      <div className="fl-field">
        <label>Notes</label>
        <textarea className="si-note" rows={3} value={entry.note || ''}
          onChange={e => onChange({ note: e.target.value })} />
      </div>

      <div className="fl-safety-toggle-row">
        <label className="fl-safety-toggle">
          <input type="checkbox" checked={!!entry.safetyRisk}
            onChange={e => onChange({ safetyRisk: e.target.checked, safetyNote: e.target.checked ? entry.safetyNote : '' })} />
          <span>⚠ Trip hazard or safety risk</span>
        </label>
        {entry.safetyRisk && (
          <input className="fl-text-input fl-safety-note" type="text" placeholder="What's the hazard, and where"
            value={entry.safetyNote || ''} onChange={e => onChange({ safetyNote: e.target.value })} />
        )}
        {invalid && <div className="fl-safety-error">A safety note is required when a safety risk is flagged.</div>}
      </div>

      <PhotoUpload
        photos={entry.photos}
        projectId={projectId}
        onAdd={(p) => onChange({ photos: [...(Array.isArray(entry.photos) ? entry.photos : []), p] })}
        onUpdate={(tempId, patch) => onChange({
          photos: (Array.isArray(entry.photos) ? entry.photos : []).map(p => p.id === tempId ? { ...p, ...patch } : p),
        })}
        onRemove={(id) => onChange({ photos: (Array.isArray(entry.photos) ? entry.photos : []).filter(p => p.id !== id) })}
      />

      <button type="button" className="fl-remove-btn" onClick={() => setConfirmRemove(true)}>Remove</button>
      {confirmRemove && (
        <ConfirmModal
          title="Remove floor covering?"
          message={`Remove "${entry.product || 'this entry'}" from the flooring register?`}
          confirmLabel="Remove"
          danger
          onConfirm={() => { setConfirmRemove(false); onRemove(); }}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
    </div>
  );
}

export default function FlooringForm({ project, onSave, onBack }) {
  const [rows, setRows] = useState(() => (Array.isArray(project.flooring) ? project.flooring.map(r => ({ ...r })) : []));
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [invalidIds, setInvalidIds] = useState([]);
  const dirty = useRef(false);

  useEffect(() => {
    const handler = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const addRow = () => {
    const e = emptyEntry();
    dirty.current = true;
    setRows(prev => [...prev, e]);
    setExpandedId(e.id);
  };

  const updateRow = (id, patch) => {
    dirty.current = true;
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
    setInvalidIds(prev => prev.filter(x => x !== id));
  };

  const removeRow = (id) => {
    dirty.current = true;
    setRows(prev => prev.filter(r => r.id !== id));
    if (expandedId === id) setExpandedId(null);
    setInvalidIds(prev => prev.filter(x => x !== id));
  };

  const handleBack = () => {
    if (dirty.current) { setConfirmDiscard(true); return; }
    onBack();
  };

  const handleSave = async () => {
    const bad = rows.filter(r => r.safetyRisk && !(r.safetyNote || '').trim()).map(r => r.id);
    if (bad.length) {
      setInvalidIds(bad);
      setExpandedId(bad[0]);
      showToast('Add a safety note for every flagged safety risk before saving', 'error');
      return;
    }
    setSaving(true);
    try {
      await onSave({ flooring: rows });
      dirty.current = false;
      showToast('Flooring saved', 'success');
    } catch (err) {
      console.error('Flooring save failed:', err);
      showToast('Save failed — please try again', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="as-screen">
      {confirmDiscard && (
        <ConfirmModal
          title="Discard changes?"
          message="You have unsaved edits to Internal Flooring. Leaving now will discard them."
          confirmLabel="Discard"
          danger
          onConfirm={() => { dirty.current = false; setConfirmDiscard(false); onBack(); }}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}

      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">🪵 Internal Flooring</div>
          <div className="s2s-header-sub">{project.projectName || project.clientName || 'Project'}</div>
        </div>
        <div style={{ width: 60 }} />
      </div>

      <div className="as-content" style={{ paddingBottom: 110 }}>
        {rows.length === 0 && (
          <div className="fl-empty">No floor coverings recorded. Add each product and where it is.</div>
        )}

        {rows.map(entry => (
          <FlooringEntryCard
            key={entry.id}
            entry={entry}
            expanded={expandedId === entry.id}
            invalid={invalidIds.includes(entry.id)}
            onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
            onChange={patch => updateRow(entry.id, patch)}
            onRemove={() => removeRow(entry.id)}
            projectId={project.firestoreId}
          />
        ))}

        <button className="fl-add-btn" onClick={addRow}>＋ Add floor covering</button>
      </div>

      <div className="si-save-bar">
        <button className="si-save-btn" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
